/**
 * DynamicTrackingScripts - Injects GTM/GA4/Clarity scripts based on platform_settings
 * Uses settings from database instead of environment variables
 * 
 * SECURITY: All tracking IDs are validated before injection to prevent XSS attacks
 */
import { useEffect, useMemo, useState } from 'react';
import { useSEOAnalyticsSettings } from '@/hooks/useSEOAnalyticsSettings';
import { initializeConsentDefaults, shouldInjectTrackingScripts } from '@/lib/gtm';

// ============== SECURITY: Strict validation patterns ==============
const TRACKING_ID_PATTERNS = {
  gtm: /^GTM-[A-Z0-9]{7,10}$/,
  ga4: /^G-[A-Z0-9]{10,12}$/,
  fbPixel: /^[0-9]{15,16}$/,
  linkedIn: /^[0-9]{6,10}$/,
  clarity: /^[a-z0-9]{10,12}$/,
} as const;

function isValidTrackingId(type: keyof typeof TRACKING_ID_PATTERNS, id: string): boolean {
  if (!id) return false;
  return TRACKING_ID_PATTERNS[type].test(id);
}

function sanitizeAlphanumericId(id: string): string {
  return id.replace(/[^A-Z0-9-]/gi, '').substring(0, 20);
}

function sanitizeNumericId(id: string): string {
  return id.replace(/[^0-9]/g, '').substring(0, 20);
}

function sanitizeLowercaseId(id: string): string {
  return id.replace(/[^a-z0-9]/g, '').substring(0, 12);
}

export function DynamicTrackingScripts() {
  const { config, isLoading, error } = useSEOAnalyticsSettings();
  const [scriptsInjected, setScriptsInjected] = useState(false);
  const [allowFallbackBootstrap, setAllowFallbackBootstrap] = useState(false);
  const shouldSkipScriptInjection = typeof window !== 'undefined' && !shouldInjectTrackingScripts();
  const shouldForceFallbackConfig = isLoading || !!error;
  const stableConfig = useMemo(() => ({
    ...config,
    ...(shouldForceFallbackConfig ? {
      gtmEnabled: true,
      gtmId: 'GTM-5SNSJT86',
      defaultConsentDeny: false,
      consentModeEnabled: true,
    } : {}),
    gtmEnabled: (shouldForceFallbackConfig ? true : config.gtmEnabled) && !!(shouldForceFallbackConfig ? 'GTM-5SNSJT86' : config.gtmId),
    ga4Enabled: config.ga4Enabled && !!config.ga4MeasurementId,
  }), [config, shouldForceFallbackConfig]);

  useEffect(() => {
    if (!isLoading) {
      setAllowFallbackBootstrap(false);
      return;
    }

    const timer = window.setTimeout(() => {
      setAllowFallbackBootstrap(true);
    }, 1500);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isLoading]);

  useEffect(() => {
    const canBootstrap = !isLoading || !!error || allowFallbackBootstrap;
    if (!canBootstrap || scriptsInjected || shouldSkipScriptInjection) return;

    // Initialize consent defaults FIRST (before any tracking scripts)
    if (stableConfig.consentModeEnabled) {
      initializeConsentDefaults(stableConfig.defaultConsentDeny);

      // When fallback/public config allows analytics by default, emit an
      // immediate granted update so the first pageview is not suppressed by
      // an earlier denied baseline from a stale session or delayed settings.
      if (!stableConfig.defaultConsentDeny && typeof window !== 'undefined') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(['consent', 'update', {
          ad_storage: 'denied',
          ad_user_data: 'denied',
          ad_personalization: 'denied',
          analytics_storage: 'granted',
          functionality_storage: 'granted',
          personalization_storage: 'denied',
          security_storage: 'granted',
        }]);
      }
    }

    // Inject GTM if enabled AND valid
    if (stableConfig.gtmEnabled && stableConfig.gtmId && isValidTrackingId('gtm', stableConfig.gtmId)) {
      injectGTM(sanitizeAlphanumericId(stableConfig.gtmId), stableConfig.gtmDebugMode);
    } else if (stableConfig.gtmEnabled && stableConfig.gtmId) {
      console.warn('[GTM] Invalid container ID format, skipping injection:', stableConfig.gtmId);
    }

    // Inject GA4 directly if enabled (and not using GTM)
    if (stableConfig.ga4Enabled && stableConfig.ga4MeasurementId && !stableConfig.gtmEnabled) {
      if (isValidTrackingId('ga4', stableConfig.ga4MeasurementId)) {
        injectGA4(sanitizeAlphanumericId(stableConfig.ga4MeasurementId), stableConfig.ga4DebugMode);
      } else {
        console.warn('[GA4] Invalid measurement ID format, skipping injection:', stableConfig.ga4MeasurementId);
      }
    }

    // Inject Facebook Pixel if enabled AND valid
    if (stableConfig.fbPixelEnabled && stableConfig.fbPixelId) {
      if (isValidTrackingId('fbPixel', stableConfig.fbPixelId)) {
        injectFacebookPixel(sanitizeNumericId(stableConfig.fbPixelId));
      } else {
        console.warn('[FB Pixel] Invalid pixel ID format, skipping injection:', stableConfig.fbPixelId);
      }
    }

    // Inject LinkedIn Insight Tag if enabled AND valid
    if (stableConfig.linkedInEnabled && stableConfig.linkedInPartnerId) {
      if (isValidTrackingId('linkedIn', stableConfig.linkedInPartnerId)) {
        injectLinkedIn(sanitizeNumericId(stableConfig.linkedInPartnerId));
      } else {
        console.warn('[LinkedIn] Invalid partner ID format, skipping injection:', stableConfig.linkedInPartnerId);
      }
    }

    // Inject Microsoft Clarity if enabled AND valid
    if (stableConfig.clarityEnabled && stableConfig.clarityProjectId) {
      if (isValidTrackingId('clarity', stableConfig.clarityProjectId)) {
        injectClarity(sanitizeLowercaseId(stableConfig.clarityProjectId));
      } else {
        console.warn('[Clarity] Invalid project ID format, skipping injection:', stableConfig.clarityProjectId);
      }
    }

    setScriptsInjected(true);
  }, [allowFallbackBootstrap, error, isLoading, scriptsInjected, shouldSkipScriptInjection, stableConfig]);

  // Inject verification meta tags
  useEffect(() => {
    const canInjectMeta = !isLoading || !!error || allowFallbackBootstrap;
    if (!canInjectMeta || shouldSkipScriptInjection) return;

    if (stableConfig.gscEnabled && stableConfig.gscVerificationMeta) {
      const sanitized = stableConfig.gscVerificationMeta.replace(/[^a-zA-Z0-9_-]/g, '').substring(0, 100);
      if (sanitized) injectMetaTag('google-site-verification', sanitized);
    }

    if (stableConfig.bingEnabled && stableConfig.bingVerificationMeta) {
      const sanitized = stableConfig.bingVerificationMeta.replace(/[^a-zA-Z0-9]/g, '').substring(0, 100);
      if (sanitized) injectMetaTag('msvalidate.01', sanitized);
    }

    if (stableConfig.yandexVerification) {
      const sanitized = stableConfig.yandexVerification.replace(/[^a-zA-Z0-9]/g, '').substring(0, 100);
      if (sanitized) injectMetaTag('yandex-verification', sanitized);
    }

    if (stableConfig.pinterestVerification) {
      const sanitized = stableConfig.pinterestVerification.replace(/[^a-zA-Z0-9]/g, '').substring(0, 100);
      if (sanitized) injectMetaTag('p:domain_verify', sanitized);
    }
  }, [allowFallbackBootstrap, error, isLoading, shouldSkipScriptInjection, stableConfig]);

  return null;
}

// ============== Script Injection Functions ==============

function injectGTM(containerId: string, debugMode: boolean) {
  if (document.getElementById('gtm-script')) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  if (debugMode) {
    window.dataLayer.push({ 'gtm.debug': true });
  }

  const script = document.createElement('script');
  script.id = 'gtm-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.insertBefore(script, document.head.firstChild);

  const noscript = document.createElement('noscript');
  noscript.id = 'gtm-noscript';
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);

  console.log(`[GTM] Initialized with container: ${containerId}`);
}

function injectGA4(measurementId: string, debugMode: boolean) {
  if (document.getElementById('ga4-script')) return;

  const script = document.createElement('script');
  script.id = 'ga4-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(args);
  }
  window.gtag = gtag;

  gtag('js', new Date());
  gtag('config', measurementId, {
    debug_mode: debugMode,
    send_page_view: true,
  });

  console.log(`[GA4] Initialized with ID: ${measurementId}`);
}

function injectFacebookPixel(pixelId: string) {
  if (document.getElementById('fb-pixel-script')) return;
  if ((window as any).fbq) return;

  const script = document.createElement('script');
  script.id = 'fb-pixel-script';
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);

  console.log(`[FB Pixel] Initialized with ID: ${pixelId}`);
}

function injectLinkedIn(partnerId: string) {
  if (document.getElementById('linkedin-script')) return;

  const script = document.createElement('script');
  script.id = 'linkedin-script';
  script.innerHTML = `
    _linkedin_partner_id = "${partnerId}";
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    (function(l) {
    if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
    window.lintrk.q=[]}
    var s = document.getElementsByTagName("script")[0];
    var b = document.createElement("script");
    b.type = "text/javascript";b.async = true;
    b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
    s.parentNode.insertBefore(b, s);})(window.lintrk);
  `;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.alt = '';
  img.src = `https://px.ads.linkedin.com/collect/?pid=${partnerId}&fmt=gif`;
  noscript.appendChild(img);
  document.body.appendChild(noscript);

  console.log(`[LinkedIn] Initialized with Partner ID: ${partnerId}`);
}

function injectClarity(projectId: string) {
  if (document.getElementById('clarity-script')) return;
  if ((window as any).clarity) return;

  const script = document.createElement('script');
  script.id = 'clarity-script';
  script.innerHTML = `
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${projectId}");
  `;
  document.head.appendChild(script);

  console.log(`[Clarity] Initialized with Project ID: ${projectId}`);
}

function injectMetaTag(name: string, content: string) {
  const existing = document.querySelector(`meta[name="${name}"]`);
  if (existing) {
    existing.setAttribute('content', content);
    return;
  }

  const meta = document.createElement('meta');
  meta.name = name;
  meta.content = content;
  document.head.appendChild(meta);
}
